import { useState } from 'react';
import { MoreHorizontal, Edit, X, AlertTriangle } from 'lucide-react';
import { devLog } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditLeaveRequestDialog } from './EditLeaveRequestDialog';
import { useLeaveMutations } from '@/hooks/leave/useLeaveMutations';
import type { LeaveRequest } from '@/services/leave/types';

interface LeaveRequestActionsProps {
  leaveRequest: LeaveRequest;
}

// Helper to check if a date is in the future
const isDateInFuture = (dateStr: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  return targetDate > today;
};

export const LeaveRequestActions = ({ leaveRequest }: LeaveRequestActionsProps) => {
  const { cancelMyLeaveRequest, cancelApprovedLeaveRequest } = useLeaveMutations();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Determine what actions are available
  const isPending = leaveRequest.status === 'pending';
  const isApproved = leaveRequest.status === 'approved';
  const canModifyApproved = isApproved && isDateInFuture(leaveRequest.start_date);
  
  // Can edit/cancel if pending OR if approved and not yet started
  const canEdit = isPending || canModifyApproved;
  
  devLog('[LeaveRequestActions] Render:', {
    requestId: leaveRequest.id,
    status: leaveRequest.status,
    canEdit,
  });
  
  if (!canEdit) {
    return null;
  }

  const handleCancel = async () => {
    devLog('[LeaveRequestActions] handleCancel:', leaveRequest.id);
    
    try {
      if (isPending) {
        devLog('[LeaveRequestActions] Cancelling PENDING request');
        await cancelMyLeaveRequest.mutateAsync(leaveRequest.id);
      } else if (canModifyApproved) {
        devLog('[LeaveRequestActions] Cancelling APPROVED request');
        await cancelApprovedLeaveRequest.mutateAsync(leaveRequest.id);
      }
      setShowCancelDialog(false);
    } catch (error) {
      console.error('[LeaveRequestActions] Failed to cancel leave request:', error);
    }
  };

  const isCancelling = cancelMyLeaveRequest.isPending || cancelApprovedLeaveRequest.isPending;

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              devLog('[LeaveRequestActions] Edit clicked:', leaveRequest.id);
              setShowEditDialog(true);
              setIsDropdownOpen(false);
            }}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              devLog('[LeaveRequestActions] Cancel clicked:', leaveRequest.id);
              setShowCancelDialog(true);
              setIsDropdownOpen(false);
            }}
            className="text-red-600"
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditLeaveRequestDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        leaveRequest={leaveRequest}
      />

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canModifyApproved ? 'Cancel Approved Leave' : 'Cancel Leave Request'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {canModifyApproved ? (
                  <>
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium">This is an approved leave request</p>
                        <p className="mt-1">
                          Cancelling will restore <strong>{leaveRequest.requested_days || 'the'} days</strong> back to your leave balance.
                        </p>
                      </div>
                    </div>
                    <p>Are you sure you want to cancel this approved leave request?</p>
                  </>
                ) : (
                  <p>Are you sure you want to cancel this leave request? This action cannot be undone.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling...' : 'Yes, cancel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
